import { FlagNames, isFeatureEnabled, Stages } from '@amzn/aws-tc-ilt-common';
import { NodeEnvironment, getNodeEnvironment } from './nodeEnvironment';
export const APPLICATION_NAME = 'myClass';
export const PRIVACY_PAGE_LINK = 'https://aws.amazon.com/privacy';
export const TERMS_PAGE_LINK = 'https://aws.amazon.com/terms';
export const CERTIFICATION_PAGE = 'https://aws.amazon.com/certification/';
export const CLIENT_ID = '136u1ukf9ea43hpqqbo05v7es1';
export const LOGOUT_URL = `https%3A%2F%2Fus-east-1.auth.skillbuilder.aws%2Flogout%3Fclient_id%3D${CLIENT_ID}%26logout_uri%3Dhttps%3A%2F%2Flogin.us-east-1.auth.skillbuilder.aws%2Flogout%3Fclient_id%3D${CLIENT_ID}`;
export const EMAIL_VERIFICATION_ERROR = 'EmailAddress is not verified, verification required';

export enum APPLICATION_URL {
    DEVELOPMENT = 'http://localhost:3000',
    BETA = 'https://beta.myclass.skillbuilder.training.aws.dev',
    GAMMA = 'https://myclass.skillbuilder.training.aws.dev',
    PROD = 'https://myclass.skillbuilder.aws',
}

const endpoints = {
    // Point dev to local fastify server and others to beta supergraph
    [NodeEnvironment.TEST]: `${APPLICATION_URL.DEVELOPMENT}/graphql`,
    [NodeEnvironment.DEVELOPMENT]: `${APPLICATION_URL.DEVELOPMENT}/graphql`,
    [NodeEnvironment.BETA]: `${APPLICATION_URL.BETA}/graphql`,
    [NodeEnvironment.GAMMA]: `${APPLICATION_URL.GAMMA}/graphql`,
    [NodeEnvironment.PROD]: `${APPLICATION_URL.PROD}/graphql`,
};

export const getClientEndpoint = () => {
    return endpoints[getNodeEnvironment()] || endpoints[NodeEnvironment.TEST];
};

/**
 * GBL-curated catalog group holding the premium content list for bolt-on Cloud Quest classes.
 *
 * Only defined where GBL maintains a group. Beta has none, and pointing it at another stage's group
 * would not resolve against the beta supergraph, so beta shows an empty premium list.
 */
const premiumContentGroupIds: Partial<Record<NodeEnvironment, string>> = {
    [NodeEnvironment.GAMMA]: 'ddf51726-17de-4e33-8e49-daaf56d1ff66',
    [NodeEnvironment.PROD]: 'be7fdcc3-667d-4dcb-bea4-91d936570e92',
};

export const getPremiumContentGroupId = (): string | undefined => {
    return premiumContentGroupIds[getNodeEnvironment()];
};

/** Drives the $queryLocalizedCourseCatalogItem variable the query params above are gated on. */
export const isLocalizedCourseCatalogItemEnabled = (): boolean =>
    !!isFeatureEnabled(FlagNames.LOCALIZED_COURSE_CATALOG_ITEM, getNodeEnvironment() as Stages);

/**
 * Gates fetching the learner's assigned-learning-goal activities (via the
 * learningGoalTranscriptsByUserId query) and surfacing them on the dashboard.
 */
export const isRetrieveLearningGoalAssignmentsEnabled = (): boolean =>
    !!isFeatureEnabled(FlagNames.RETRIEVE_LEARNING_GOAL_ASSIGNMENTS, getNodeEnvironment() as Stages);

export enum RoleType {
    SERVICE = 'SERVICE',
    SUPER_USER = 'SUPER_USER',
    INTERNAL_EMPLOYEE = 'INTERNAL_EMPLOYEE',
    LEARNING_ADMIN = 'LEARNING_ADMIN',
    POST_AUTH_PRE_VIAS = 'POST_AUTH_PRE_VIAS',
    LEARNING_ACCOUNT_IT_CONTACT = 'LEARNING_ACCOUNT_IT_CONTACT',
    LEARNER = 'LEARNER',
}

export enum RoleV2 {
    LEARNER = 'LEARNER',
}

export enum AssociationResourceType {
    ORGANIZATION = 'Organization',
    TEAM = 'Team',
}

export enum AuthEndpoint {
    SIGN_IN = 'signIn',
    SIGN_OUT = 'signOut',
    REFRESH_TOKEN = 'refreshToken',
}

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

// content_configs[].content_type value that marks a class as bolt-on Cloud Quest
export const CONTENT_TYPE_CLOUD_QUEST = 'cloud_quest';

// Fields shared by every learning activity query.
// each consumer appends the selection it needs below.
const LEARNING_ACTIVITY_BASE_PARAMS = `
    pk
    activity_name
    activity_status
    activity_modality
    activity_audience
    class_size
    customers {
        number_of_students_committed
        organization {
            id
        }
        team {
            id
        }
    }
    classrooms_student_url
    course_name
    delivery_language
    delivery_sessions {
        start_timestamp
        end_timestamp
    }
    delivery_address_1
    delivery_address_2
    room
    delivery_city
    delivery_state
    delivery_postal_code
    delivery_country
    delivery_timezone
    lms_type
    program
    v_ilt_id
    v_ilt_type
    waitlist_enabled
    course_catalog_item {
        id
        versionedId
        name
        description
        domain {
            valueDisplayString
        }
        skillLevel {
            valueDisplayString
        }
        objectives {
            values
        }
        intendedAudience {
            values
        }
        prerequisites {
            value
        }
        duration {
            unit
            value
        }
        outline
        children {
            nodes {
                outline
            }
        }
    }
    localized_course_catalog_item @include(if: $queryLocalizedCourseCatalogItem) {
        id
        versionedId
        name
        description
        domain {
            valueDisplayString
        }
        skillLevel {
            valueDisplayString
        }
        objectives {
            values
        }
        intendedAudience {
            values
        }
        prerequisites {
            value
        }
        duration {
            unit
            value
        }
        outline
        children {
            nodes {
                outline
            }
        }
    }
    course_catalog_item_limited @include(if: $queryCourseCatalogItemLimited) {
        id
        versionedId
        name
        description
        domain {
            valueDisplayString
        }
        skillLevel {
            valueDisplayString
        }
        objectives {
            values
        }
        intendedAudience {
            values
        }
        prerequisites {
            value
        }
        duration {
            unit
            value
        }
    }
    registrationCounter @include(if: $queryRegistrationCounter)
    student_notes {
        description
    }
    pre_work_instruction {
        description
    }
    registrationCounterPerOrganization @include(if: $queryRegistrationCounter) {
        organization {
            id
        }
        registrationCounter
    }
    learning_goal_ids
`;

// Dashboard and paginated list views. content_type is all CourseBadges needs to decide
// whether to render the Cloud Quest badge, and this fragment is inlined twice in the
// learningActivitiesByUserId query, so every field here costs double on the wire.
export const LEARNING_ACTIVITY_PARAMS = `
    ${LEARNING_ACTIVITY_BASE_PARAMS}
    content_configs {
        content_type
    }
`;

/**
 * Learning-activity selection for activities reached through the learning-goal path
 * (`learningGoalTranscriptsByUserId -> learningGoal -> learningActivities`).
 *
 * This deliberately omits the registration-management extension fields (`registrationCounter`,
 * `registrationCounterPerOrganization`, `customers`). Those are contributed to `LearningActivity`
 * by IltRegistrationManagementService; requiring them through the federated LearningGoal
 * (DigitalTMS/Catalog -> IltLearningActivityDataService, keyed by pk) makes the router reject the
 * document with a 400. They are also not meaningful here — seat/registration data comes from the
 * learner's registrations, not from goal-listed activities. Catalog + schedule fields are enough to
 * build the dashboard card.
 */
export const LEARNING_GOAL_ACTIVITY_PARAMS = `
    pk
    activity_name
    activity_status
    activity_modality
    activity_audience
    class_size
    classrooms_student_url
    course_name
    delivery_language
    delivery_sessions {
        start_timestamp
        end_timestamp
    }
    delivery_address_1
    delivery_address_2
    room
    delivery_city
    delivery_state
    delivery_postal_code
    delivery_country
    delivery_timezone
    lms_type
    program
    v_ilt_id
    v_ilt_type
    waitlist_enabled
    course_catalog_item {
        id
        versionedId
        name
        description
        domain {
            valueDisplayString
        }
        skillLevel {
            valueDisplayString
        }
        objectives {
            values
        }
        intendedAudience {
            values
        }
        prerequisites {
            value
        }
        duration {
            unit
            value
        }
        outline
        children {
            nodes {
                outline
            }
        }
    }
    student_notes {
        description
    }
    pre_work_instruction {
        description
    }
    learning_goal_ids
    content_configs {
        content_type
    }
`;

// Class PDP. Needs the full config to render the Cloud Quest tab and the access countdown.
export const LEARNING_ACTIVITY_PDP_PARAMS = `
    ${LEARNING_ACTIVITY_BASE_PARAMS}
    content_configs {
        content_type
        access_start_timestamp
        access_end_timestamp
        related_catalog_item {
            id
            versionedId
        }
    }
`;
